// Platform console — platform owner only.
// Invite teachers with single-use codes (emailed automatically), see every
// school with its teachers and students, suspend / rename / delete schools.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { FiMail, FiCopy, FiSend, FiSlash, FiTrash2, FiChevronDown, FiChevronUp, FiEdit2, FiCheck, FiX, FiPower, FiUsers, FiShield, FiKey, FiHome } from 'react-icons/fi';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { BRAND, GOLD_GRADIENT, Page, PageHeader, Card, Stat, Tag, Alert, EmptyState, Button, inputStyle, labelStyle, fadeUp, stagger } from '../common/ui';

const HERO = { img: '/illustrations/lesson1/scenes/map.jpg', pos: 'center 30%', height: 640, opacity: 0.6 };
const LINE = 'rgba(249,240,231,0.12)';
const input = inputStyle(false);
const label = labelStyle(false);

function errMsg(err, fallback = 'Something went wrong') {
  const d = err?.response?.data;
  if (Array.isArray(d?.details) && d.details.length) return d.details.map((e) => e.message).join('\n');
  if (Array.isArray(d?.errors) && d.errors[0]?.msg) return d.errors[0].msg;
  return d?.error || d?.message || err?.message || fallback;
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const fmtAgo = (iso) => {
  if (!iso) return 'never';
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 30) return `${d} days ago`;
  return fmtDate(iso);
};

function copy(text) {
  if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(() => toast.success('Copied!'), () => toast.error('Copy failed'));
}

const th = { textAlign: 'left', fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: BRAND.muted, fontWeight: 800, padding: '10px 12px', background: 'rgba(249,240,231,0.05)', borderBottom: `1px solid ${LINE}`, whiteSpace: 'nowrap' };
const td = { padding: '12px 12px', borderBottom: `1px solid rgba(249,240,231,0.07)`, fontSize: '0.9rem', color: BRAND.ivory, verticalAlign: 'middle' };
const mini = (tone = 'ghost') => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: 999, border: `1px solid ${tone === 'danger' ? 'rgba(224,122,122,0.45)' : 'rgba(249,240,231,0.16)'}`, background: tone === 'danger' ? 'rgba(224,122,122,0.14)' : 'rgba(249,240,231,0.08)', color: tone === 'danger' ? '#f0a0a0' : BRAND.ivory, fontFamily: BRAND.font, fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap' });

const statusTone = { active: 'success', used: 'muted', revoked: 'danger' };

export default function PlatformConsole() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [send, setSend] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [lastCode, setLastCode] = useState(null);

  const [showUsed, setShowUsed] = useState(false);
  const [openSchool, setOpenSchool] = useState(null);
  const [busy, setBusy] = useState(null); // id of the row with an action in flight

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.get('/platform/overview');
      setData(res);
    } catch (e) {
      setError(errMsg(e, 'Unable to load the platform console'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const codes = data?.codes || [];
  const visibleCodes = useMemo(() => (showUsed ? codes : codes.filter((c) => c.status !== 'used')), [codes, showUsed]);
  const usedCount = codes.filter((c) => c.status === 'used').length;

  // ── actions ──────────────────────────────────────────────────────────────

  const generate = async (e) => {
    e.preventDefault();
    const to = email.trim().toLowerCase();
    if (!to || !to.includes('@')) { toast.error('Enter the teacher’s email address'); return; }
    setGenerating(true);
    try {
      const { data: res } = await api.post('/platform/codes', { email: to, note: note.trim(), send });
      setLastCode(res);
      if (send) {
        if (res.mail?.ok && !res.mail?.skipped) toast.success(`Invitation sent to ${to}`);
        else if (res.mail?.skipped) toast('Code created — email service not configured, share the code yourself', { icon: '✉️' });
        else toast.error(`Code created, but the email failed: ${res.mail?.error || 'unknown error'}`);
      } else {
        toast.success('Code created');
      }
      setEmail(''); setNote('');
      await load(true);
    } catch (err) { toast.error(errMsg(err)); } finally { setGenerating(false); }
  };

  const act = async (id, fn, okMsg) => {
    setBusy(id);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(true); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(null); }
  };

  const sendCode = (c) => act(c._id, () => api.post(`/platform/codes/${c._id}/send`), `Invitation re-sent to ${c.email}`);
  const revokeCode = (c) => { if (window.confirm(`Revoke ${c.code}? The teacher will not be able to register with it.`)) act(c._id, () => api.post(`/platform/codes/${c._id}/revoke`), 'Code revoked'); };
  const deleteCode = (c) => { if (window.confirm(`Delete ${c.code} permanently?`)) act(c._id, () => api.delete(`/platform/codes/${c._id}`), 'Code deleted'); };

  const toggleSchool = (s) => {
    const next = !s.isActive;
    const msg = next ? `Re-activate ${s.name}? Teachers and students will be able to log in again.` : `Suspend ${s.name}? All its teachers and students will be logged out and blocked until you re-activate it. Nothing is deleted.`;
    if (window.confirm(msg)) act(s._id, () => api.patch(`/platform/schools/${s._id}`, { isActive: next }), next ? 'School re-activated' : 'School suspended');
  };
  const renameSchool = (s, name) => act(s._id, () => api.patch(`/platform/schools/${s._id}`, { name }), 'School renamed');
  const deleteSchool = (s) => {
    const typed = window.prompt(`This permanently deletes "${s.name}": ${s.teachers.length} teacher account(s), ${s.students} student account(s) and all their progress.\n\nType DELETE to confirm.`);
    if (typed === null) return;
    if (typed.trim().toUpperCase() !== 'DELETE') { toast.error('Deletion cancelled — you must type DELETE'); return; }
    act(s._id, () => api.delete(`/platform/schools/${s._id}`), 'School deleted');
  };
  const toggleTeacher = (t) => {
    const next = !t.isActive;
    if (window.confirm(next ? `Re-activate ${t.name}?` : `Deactivate ${t.name}? They and their ${t.students} student(s) will be blocked until re-activated.`)) act(t._id, () => api.patch(`/platform/teachers/${t._id}`, { isActive: next }), next ? 'Teacher re-activated' : 'Teacher deactivated');
  };
  const deleteTeacher = (t) => {
    const typed = window.prompt(`This permanently deletes ${t.name} (${t.email}) and their ${t.students} student account(s) with all progress.\n\nType DELETE to confirm.`);
    if (typed === null) return;
    if (typed.trim().toUpperCase() !== 'DELETE') { toast.error('Deletion cancelled — you must type DELETE'); return; }
    act(t._id, () => api.delete(`/platform/teachers/${t._id}`), 'Teacher deleted');
  };

  // ── render ───────────────────────────────────────────────────────────────

  return (
    <Page hero={HERO} maxWidth={1280}>
      <PageHeader kicker="Platform owner" title="Platform console"
        subtitle="Invite teachers with single-use codes, see every school and keep control of who can use CORE for Schools." />

      {loading && <Card style={{ padding: 0, overflow: 'hidden' }}><LoadingSpinner message="Loading the console…" /></Card>}
      {!loading && error && <Alert>{error}</Alert>}

      {!loading && !error && data && (
        <>
          <motion.div variants={stagger} initial="hidden" animate="visible" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14 }}>
            <motion.div variants={fadeUp}><Stat icon="🏫" value={data.stats.schools} label={`schools · ${data.stats.activeSchools} active`} /></motion.div>
            <motion.div variants={fadeUp}><Stat icon="🧑‍🏫" value={data.stats.teachers} label="teachers" /></motion.div>
            <motion.div variants={fadeUp}><Stat icon="🎒" value={data.stats.students} label="students" /></motion.div>
            <motion.div variants={fadeUp}><Stat icon="🔑" value={data.stats.activeCodes} label="open invitations" /></motion.div>
          </motion.div>

          {/* ── invite a teacher ── */}
          <motion.section variants={fadeUp} initial="hidden" animate="visible" style={{ marginTop: 26 }}>
            <Card style={{ padding: 24 }}>
              <SectionTitle icon={FiKey} title="Invite a teacher" />
              <p style={{ margin: '0 0 16px', color: BRAND.muted, fontSize: '0.9rem', lineHeight: 1.55, maxWidth: 760 }}>
                Each code is personal and works once. The teacher receives a branded email with the code and a registration link; when they register with their school email, their school appears below automatically — colleagues with the same email domain are grouped together.
              </p>
              <form onSubmit={generate} style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1.2fr) minmax(200px, 1fr) auto', gap: 12, alignItems: 'end' }} className="plat-form">
                <style>{`@media (max-width: 760px){ .plat-form{ grid-template-columns: 1fr !important; } }`}</style>
                <div>
                  <label style={label}>Teacher’s email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teacher@school.k12.oh.us" style={input} disabled={generating} autoComplete="off" />
                </div>
                <div>
                  <label style={label}>Note (optional)</label>
                  <input type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="e.g. Tipp City — pilot 2026/27" style={input} disabled={generating} />
                </div>
                <Button type="submit" variant="gold" disabled={generating} style={{ height: 46 }}>
                  {generating ? 'Working…' : <><FiSend size={16} /> {send ? 'Create & send' : 'Create code'}</>}
                </Button>
              </form>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: '0.86rem', color: BRAND.muted, fontWeight: 600, cursor: 'pointer' }}>
                <input type="checkbox" checked={send} onChange={(e) => setSend(e.target.checked)} /> Email the invitation automatically
              </label>

              <AnimatePresence>
                {lastCode && (
                  <motion.div key={lastCode.code._id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    style={{ marginTop: 18, padding: '16px 18px', borderRadius: 16, background: BRAND.navy900, border: `1px solid ${BRAND.line}`, color: BRAND.ivory, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14 }}>
                    <div style={{ flex: '1 1 220px' }}>
                      <div style={{ fontSize: '0.7rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: BRAND.goldLight, fontWeight: 800 }}>New invitation for {lastCode.code.email}</div>
                      <div style={{ fontFamily: 'Menlo, Consolas, monospace', fontSize: '1.5rem', fontWeight: 700, letterSpacing: 3, color: BRAND.goldLight, marginTop: 4 }}>{lastCode.code.code}</div>
                      <div style={{ fontSize: '0.78rem', color: BRAND.muted, marginTop: 4, wordBreak: 'break-all' }}>{lastCode.registerUrl}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button variant="outline" size="sm" onClick={() => copy(lastCode.code.code)}><FiCopy size={14} /> Copy code</Button>
                      <Button variant="outline" size="sm" onClick={() => copy(lastCode.registerUrl)}><FiCopy size={14} /> Copy link</Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          </motion.section>

          {/* ── invitation codes ── */}
          <motion.section variants={fadeUp} initial="hidden" animate="visible" style={{ marginTop: 22 }}>
            <Card style={{ padding: 24 }}>
              <SectionTitle icon={FiMail} title="Invitation codes" right={usedCount > 0 && (
                <button type="button" style={mini()} onClick={() => setShowUsed((v) => !v)}>{showUsed ? 'Hide used codes' : `Show ${usedCount} used code${usedCount === 1 ? '' : 's'}`}</button>
              )} />
              {visibleCodes.length === 0 ? (
                <p style={{ margin: 0, color: BRAND.muted, fontSize: '0.9rem' }}>{codes.length ? 'No open invitations. Every code has been used.' : 'No codes yet — invite your first teacher above.'}</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={th}>Code</th><th style={th}>Teacher</th><th style={th}>Note</th><th style={th}>Status</th><th style={th}>Created</th><th style={th} /></tr></thead>
                    <tbody>
                      {visibleCodes.map((c) => (
                        <tr key={c._id}>
                          <td style={td}><code style={{ fontFamily: 'Menlo, Consolas, monospace', fontWeight: 700, letterSpacing: 1 }}>{c.code}</code></td>
                          <td style={td}>
                            <div style={{ fontWeight: 700 }}>{c.email}</div>
                            <div style={{ fontSize: '0.74rem', color: BRAND.muted }}>
                              {c.status === 'used' && c.usedBy ? `Registered as ${c.usedBy.name} · ${fmtDate(c.usedAt)}` : c.sentAt ? `Sent ${fmtAgo(c.sentAt)}${c.sendCount > 1 ? ` (×${c.sendCount})` : ''}` : 'Not emailed yet'}
                            </div>
                          </td>
                          <td style={{ ...td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: c.note ? BRAND.ivory : BRAND.muted }} title={c.note}>{c.note || '—'}</td>
                          <td style={td}><Tag tone={statusTone[c.status] || 'muted'}>{c.status}</Tag></td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(c.createdAt)}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                            {c.status === 'active' && (
                              <span style={{ display: 'inline-flex', gap: 6 }}>
                                <button type="button" style={mini()} onClick={() => copy(c.code)} title="Copy code"><FiCopy size={13} /></button>
                                <button type="button" style={mini()} disabled={busy === c._id} onClick={() => sendCode(c)}><FiSend size={13} /> {c.sentAt ? 'Resend' : 'Send email'}</button>
                                <button type="button" style={mini('danger')} disabled={busy === c._id} onClick={() => revokeCode(c)}><FiSlash size={13} /> Revoke</button>
                              </span>
                            )}
                            {c.status !== 'active' && (
                              <button type="button" style={mini('danger')} disabled={busy === c._id} onClick={() => deleteCode(c)}><FiTrash2 size={13} /> Delete</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </motion.section>

          {/* ── schools ── */}
          <motion.section variants={fadeUp} initial="hidden" animate="visible" style={{ marginTop: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ width: 36, height: 36, borderRadius: 11, background: GOLD_GRADIENT, color: BRAND.navy900, display: 'grid', placeItems: 'center' }}><FiHome size={18} /></span>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 900, margin: 0, color: BRAND.ivory }}>Schools</h2>
              <span style={{ fontSize: '0.86rem', color: BRAND.muted }}>— every school on the platform with its teachers</span>
            </div>

            {data.schools.length === 0 && <EmptyState icon="🏫" title="No schools yet" text="Schools appear here as soon as an invited teacher registers." />}

            <div style={{ display: 'grid', gap: 12 }}>
              {data.schools.map((s) => (
                <SchoolCard key={s._id} school={s} open={openSchool === s._id} onToggle={() => setOpenSchool(openSchool === s._id ? null : s._id)}
                  busy={busy} onSuspend={() => toggleSchool(s)} onRename={(name) => renameSchool(s, name)} onDelete={() => deleteSchool(s)}
                  onToggleTeacher={toggleTeacher} onDeleteTeacher={deleteTeacher} />
              ))}
            </div>

            {data.unassigned?.length > 0 && (
              <Card style={{ marginTop: 14, padding: 20 }}>
                <div style={{ fontWeight: 900, color: BRAND.ivory, marginBottom: 4 }}>Teachers without a school</div>
                <p style={{ margin: '0 0 10px', fontSize: '0.84rem', color: BRAND.muted }}>Accounts created before invitation codes existed (demo or legacy).</p>
                <TeacherTable teachers={data.unassigned} busy={busy} onToggle={toggleTeacher} onDelete={deleteTeacher} />
              </Card>
            )}
          </motion.section>
        </>
      )}
    </Page>
  );
}

function SectionTitle({ icon: Icon, title, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ width: 36, height: 36, borderRadius: 11, background: GOLD_GRADIENT, color: BRAND.navy900, display: 'grid', placeItems: 'center' }}><Icon size={18} /></span>
        <h2 style={{ fontSize: '1.2rem', fontWeight: 900, margin: 0, color: BRAND.ivory }}>{title}</h2>
      </div>
      {right}
    </div>
  );
}

function SchoolCard({ school: s, open, onToggle, busy, onSuspend, onRename, onDelete, onToggleTeacher, onDeleteTeacher }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(s.name);
  useEffect(() => { setName(s.name); }, [s.name]);

  return (
    <Card style={{ padding: 0, overflow: 'hidden', opacity: s.isActive ? 1 : 0.78 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', flexWrap: 'wrap' }}>
        <button type="button" onClick={onToggle} aria-expanded={open} style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 260px', minWidth: 0, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: BRAND.font, padding: 0 }}>
          <span style={{ width: 44, height: 44, borderRadius: 12, background: s.isActive ? 'rgba(205,155,80,0.14)' : 'rgba(249,240,231,0.06)', border: `1px solid ${LINE}`, display: 'grid', placeItems: 'center', fontSize: '1.3rem', flexShrink: 0 }}>🏫</span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {editing ? (
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <input value={name} onChange={(e) => setName(e.target.value)} style={{ ...input, width: 260, padding: '6px 10px' }} autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (name.trim().length >= 2) { onRename(name.trim()); setEditing(false); } } if (e.key === 'Escape') { setName(s.name); setEditing(false); } }} />
                  <button type="button" style={mini()} onClick={() => { if (name.trim().length >= 2) { onRename(name.trim()); setEditing(false); } }}><FiCheck size={13} /></button>
                  <button type="button" style={mini()} onClick={() => { setName(s.name); setEditing(false); }}><FiX size={13} /></button>
                </span>
              ) : (
                <b style={{ fontSize: '1.05rem', color: BRAND.ivory }}>{s.name}</b>
              )}
              {!s.isActive && <Tag tone="danger">suspended</Tag>}
            </span>
            <span style={{ display: 'block', fontSize: '0.78rem', color: BRAND.muted, marginTop: 2 }}>
              {s.domain ? `@${s.domain}` : 'personal email addresses'} · since {fmtDate(s.createdAt)} · last activity {fmtAgo(s.lastActivity)}
            </span>
          </span>
        </button>

        <div style={{ display: 'flex', gap: 18, fontSize: '0.82rem', fontWeight: 700, color: BRAND.muted }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FiShield size={13} style={{ color: BRAND.gold }} />{s.teachers.length} teacher{s.teachers.length === 1 ? '' : 's'}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FiUsers size={13} style={{ color: BRAND.gold }} />{s.students} students</span>
          <span title="students who logged in during the last 30 days">{s.activeStudents30d} active</span>
        </div>

        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          <button type="button" style={mini()} onClick={() => setEditing(true)} title="Rename"><FiEdit2 size={13} /></button>
          <button type="button" style={mini(s.isActive ? 'danger' : 'ghost')} disabled={busy === s._id} onClick={onSuspend}><FiPower size={13} /> {s.isActive ? 'Suspend' : 'Re-activate'}</button>
          <button type="button" style={mini('danger')} disabled={busy === s._id} onClick={onDelete}><FiTrash2 size={13} /></button>
          <button type="button" style={mini()} onClick={onToggle}>{open ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}</button>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="body" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }} style={{ overflow: 'hidden' }}>
            <div style={{ padding: '0 20px 18px', borderTop: `1px solid ${LINE}` }}>
              <TeacherTable teachers={s.teachers} busy={busy} onToggle={onToggleTeacher} onDelete={onDeleteTeacher} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

function TeacherTable({ teachers, busy, onToggle, onDelete }) {
  if (!teachers.length) return <p style={{ margin: '12px 0 0', color: BRAND.muted, fontSize: '0.88rem' }}>No teachers.</p>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={th}>Teacher</th><th style={th}>Students</th><th style={th}>Lessons done</th><th style={th}>Quiz attempts</th><th style={th}>Last login</th><th style={th}>Joined</th><th style={th} /></tr></thead>
        <tbody>
          {teachers.map((t) => (
            <tr key={t._id} style={{ opacity: t.isActive ? 1 : 0.6 }}>
              <td style={td}>
                <div style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>{t.name}{!t.isActive && <Tag tone="danger">off</Tag>}</div>
                <div style={{ fontSize: '0.76rem', color: BRAND.muted }}>{t.email}</div>
              </td>
              <td style={td}>{t.students} <span style={{ color: BRAND.muted, fontSize: '0.76rem' }}>({t.activeStudents30d} active)</span></td>
              <td style={td}>{t.lessonsDone}</td>
              <td style={td}>{t.quizAttempts}</td>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtAgo(t.lastLogin)}</td>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(t.createdAt)}</td>
              <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                <span style={{ display: 'inline-flex', gap: 6 }}>
                  <button type="button" style={mini(t.isActive ? 'danger' : 'ghost')} disabled={busy === t._id} onClick={() => onToggle(t)}><FiPower size={13} /> {t.isActive ? 'Deactivate' : 'Re-activate'}</button>
                  <button type="button" style={mini('danger')} disabled={busy === t._id} onClick={() => onDelete(t)}><FiTrash2 size={13} /></button>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
